import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DeliveryRow } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { IconTruck } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { ErrorState } from '@/components/States';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { ExportMenu } from '@/features/portal/ExportMenu';
import { usePortal } from '@/features/portal/PortalContext';
import { OrderStatusBadge, PaymentStatusBadge } from '@/features/portal/badges';
import { fetchDeliveries, portalKeys } from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { amountSign, formatCount, formatMoney, formatPosDate, formatPosTime } from '@/lib/format';

export function DeliveriesPage() {
  const { currency, filters } = usePortal();
  const range = useMemo(() => toReportRange(filters.filters), [filters.filters]);

  const deliveriesQuery = useQuery({
    queryKey: portalKeys.deliveries(range),
    queryFn: ({ signal }) => fetchDeliveries(range, signal),
  });

  const reportCurrency = deliveriesQuery.data?.currency || currency;

  const columns = useMemo<Array<Column<DeliveryRow>>>(
    () => [
      {
        key: 'order',
        header: 'Order',
        cell: (row) => (
          <div>
            <p className="whitespace-nowrap font-medium text-slate-900">{row.orderNumber}</p>
            <p className="mt-0.5 whitespace-nowrap text-xs text-slate-400">
              {formatPosDate(row.date)} · {formatPosTime(row.time)}
            </p>
          </div>
        ),
      },
      {
        key: 'customer',
        header: 'Customer',
        cell: (row) => <span className="text-slate-700">{row.customerName || '—'}</span>,
      },
      {
        key: 'deliveryBoy',
        header: 'Delivered by',
        visibility: 'hidden sm:table-cell',
        cell: (row) => <span className="text-slate-500">{row.deliveryBoy || 'Unassigned'}</span>,
      },
      {
        key: 'payment',
        header: 'Payment',
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <PaymentStatusBadge status={row.paymentStatus} />
            <span className="text-xs text-slate-400">{row.paymentMethod || '—'}</span>
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        visibility: 'hidden md:table-cell',
        cell: (row) => <OrderStatusBadge status={row.status} />,
      },
      {
        key: 'deliveryCharge',
        header: 'Delivery fee',
        align: 'right',
        visibility: 'hidden lg:table-cell',
        cellClassName: 'tabular-nums',
        cell: (row) => formatMoney(row.deliveryCharge, reportCurrency),
      },
      {
        key: 'total',
        header: 'Total',
        align: 'right',
        cellClassName: 'tabular-nums font-medium text-slate-900',
        cell: (row) => formatMoney(row.total, reportCurrency),
      },
    ],
    [reportCurrency],
  );

  const header = (
    <PageHeader
      title="Deliveries"
      description="Delivery orders in the range, and how much of their value has actually been collected."
      actions={
        <ExportMenu
          report="deliveries"
          range={range}
          disabled={deliveriesQuery.isPending || deliveriesQuery.isError}
        />
      }
    />
  );

  // One query feeds the tiles and the table, so a failure is reported once.
  if (deliveriesQuery.isError) {
    return (
      <>
        {header}
        <div className="card">
          <ErrorState
            error={deliveriesQuery.error}
            onRetry={() => void deliveriesQuery.refetch()}
            retrying={deliveriesQuery.isFetching}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {header}

      <div className="space-y-6">
        <AsyncSection
          state={deliveriesQuery}
          skeleton={
            <KpiGrid>
              {Array.from({ length: 3 }).map((_, index) => (
                <KpiTileSkeleton key={index} />
              ))}
            </KpiGrid>
          }
        >
          {(report) => (
            <KpiGrid>
              <KpiTile label="Deliveries" value={formatCount(report.count)} />
              <KpiTile
                label="Collected"
                value={formatMoney(report.totalCollected, report.currency || currency)}
                tone="positive"
              />
              <KpiTile
                label="Pending collection"
                value={formatMoney(report.totalPending, report.currency || currency)}
                tone={amountSign(report.totalPending) > 0 ? 'negative' : 'muted'}
              />
            </KpiGrid>
          )}
        </AsyncSection>

        <SectionCard headingId="deliveries-table" title="Delivery history" count={deliveriesQuery.data?.rows.length}>
          <AsyncSection
            state={deliveriesQuery}
            skeleton={<TableSkeleton rows={6} columns={6} />}
            isEmpty={(report) => report.rows.length === 0}
            emptyIcon={<IconTruck className="h-5 w-5" />}
            emptyTitle="No deliveries in this range"
            emptyDescription="Pick a wider date range, or check another branch."
          >
            {(report) => (
              <DataTable
                caption="Delivery orders with payment status and totals"
                columns={columns}
                rows={report.rows}
                rowKey={(row, index) => `${row.orderNumber}-${index}`}
                totals={{
                  label: 'Collected',
                  cells: {
                    total: formatMoney(report.totalCollected, report.currency || currency),
                  },
                }}
              />
            )}
          </AsyncSection>
        </SectionCard>
      </div>
    </>
  );
}
